import React, { useState, useRef, useEffect } from "react";
import { Spinner } from "react-bootstrap";
import {
  ArrowLeft, CreditCard, Search, X, User,
  IndianRupee, Calendar, FileText, ImagePlus, MessageSquare, Wifi, Info,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import CommonToaster from "../Common/CommonToaster";
import { useNavigate, useParams } from "react-router-dom";
import UploadWithS3 from "../Common/UploadWithS3";
import useRazorpay from "../../hooks/useRazorpay";
import {
  AppCard, AppCardContent, AppButton, BackButton,
  UserAvatar, StatusBadge, Separator,
} from "../ui";
import "./fees-collection.scss";
import "./payment.scss";

const PAYMENT_MODES = ["UPI", "Cash", "Bank Transfer", "Razorpay (Online)"];

const initialData = {
  currentPayment: "",
  paymentMode: "",
  referenceNumber: "",
  paidTo: "",
  date: new Date().toISOString().slice(0, 10),
  refImage: null,
  nextPaymentDate: "",
  comments: "",
};

function formatCurrency(amount) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount || 0);
}

const FeeCollectionForm = ({ mode = "add" }) => {
  const { id: studentId, feeId } = useParams();
  const isEditMode = mode === "edit";

  const [studentSearch, setStudentSearch] = useState("");
  const [studentResults, setStudentResults] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [loadingStudent, setLoadingStudent] = useState(false);
  const [feeLoading, setFeeLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(isEditMode);
  const [isRazorpayTxn, setIsRazorpayTxn] = useState(false);
  const [err, setErr] = useState("");
  const [disabledSave, setDisabledSave] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [rzpProcessing, setRzpProcessing] = useState(false);
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [lastPaymentInfo, setLastPaymentInfo] = useState(null);
  const [courseAssignments, setCourseAssignments] = useState([]);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const { openCheckout, loading: rzpLoading } = useRazorpay();

  const [form, setForm] = useState(initialData);

  // Close search dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setStudentResults([]);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Fetch payment config to know if LMS payment mode is active
  useEffect(() => {
    apiClient.get("/payments/config")
      .then(({ data }) => setPaymentConfig(data))
      .catch(() => {});
  }, []);

  // ── Edit mode: load existing fee transaction ───────────────────────
  useEffect(() => {
    if (!isEditMode || !feeId) return;
    setEditLoading(true);
    apiClient.get(`/fees/${feeId}`)
      .then(({ data }) => {
        const txn = data.transaction;
        if (!txn) return;

        // Prefill student
        const stu = txn.studentId;
        if (stu) {
          setSelectedStudent(stu);
          // Also load course assignments for this student
          apiClient.get(`/course-assignments/student/${stu._id}`)
            .then(({ data: caData }) => {
              const active = (caData.assignments || []).filter((a) => a.status === "active");
              setCourseAssignments(active);
              if (txn.courseAssignmentId) {
                const matchedCa = active.find(
                  (a) => a._id === (txn.courseAssignmentId?._id || txn.courseAssignmentId)
                );
                if (matchedCa) setSelectedAssignment(matchedCa);
                // If populated object came in the transaction, use it directly
                else if (txn.courseAssignmentId?._id) {
                  setSelectedAssignment(txn.courseAssignmentId);
                }
              }
            })
            .catch(() => {});
        }

        // Prefill form
        const rawDate = txn.date ? new Date(txn.date).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10);
        const rawNextDate = txn.nextPaymentDate
          ? new Date(txn.nextPaymentDate).toISOString().slice(0, 10)
          : "";
        setForm({
          currentPayment: String(txn.currentPayment || ""),
          paymentMode   : txn.paymentMode || "",
          referenceNumber: txn.referenceNumber || "",
          paidTo        : txn.paidTo || "",
          date          : rawDate,
          refImage      : txn.refImage || null,
          nextPaymentDate: rawNextDate,
          comments      : txn.comments || "",
        });

        setIsRazorpayTxn(txn.paymentGateway === "razorpay");
      })
      .catch(() => setErr("Failed to load fee details."))
      .finally(() => setEditLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, feeId]);

  const onCancel = async () => {
    setStudentSearch("");
    setStudentResults([]);
    setSelectedStudent(null);
    setForm(initialData);
    if (form.refImage) {
      await apiClient.delete("/files/delete", { data: { key: form.refImage } });
    }
    navigate(-1);
  };

  const totalFee = selectedAssignment ? Number(selectedAssignment.totalFees || 0) : Number(selectedStudent?.totalFees || 0);
  const paidFee = selectedAssignment ? Number(selectedAssignment.paidFees || 0) : Number(selectedStudent?.paidFees || 0);
  const pendingFee = selectedAssignment ? Number(selectedAssignment.pendingFees || 0) : Number(selectedStudent?.pendingFees || 0);
  const isSelfPaced = selectedAssignment?.courseId?.courseType === 'self-paced';

  const handleStudentSearch = async (value) => {
    setStudentSearch(value);
    setErr("");
    if (value.length < 2) { setStudentResults([]); return; }
    setLoadingStudent(true);
    try {
      const q = /^\d{10}$/.test(value) ? `/students/search?mobile=${value}` : `/students/search?text=${value}`;
      const { data } = await apiClient.get(q);
      setStudentResults(data.students || []);
    } catch {
      setStudentResults([]);
      setErr("Failed to search students");
    }
    setLoadingStudent(false);
  };

  const handleStudentSelect = async (student) => {
    setFeeLoading(true);
    setStudentSearch("");
    setStudentResults([]);
    setSelectedStudent(null);
    setCourseAssignments([]);
    setSelectedAssignment(null);
    try {
      const { data } = await apiClient.get(`/students/${student._id}`);
      setSelectedStudent(data.student);
      // Fetch course assignments for this student
      try {
        const { data: caData } = await apiClient.get(`/course-assignments/student/${student._id}`);
        const active = (caData.assignments || []).filter((a) => a.status === 'active');
        setCourseAssignments(active);
        // Auto-select if only one course
        if (active.length === 1) setSelectedAssignment(active[0]);
      } catch {
        setCourseAssignments([]);
      }
    } catch {
      setErr("Failed to fetch student details");
    }
    setFeeLoading(false);
  };

  const clearStudent = () => {
    setSelectedStudent(null);
    setStudentSearch("");
    setCourseAssignments([]);
    setSelectedAssignment(null);
    setForm(initialData);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const due = (pendingFee || 0) - (parseFloat(form.currentPayment) || 0);

  const isOnlineMode = form.paymentMode === "Razorpay (Online)";

  /** Handle Razorpay checkout flow. */
  const handleRazorpayPayment = async () => {
    if (!selectedStudent) return;
    const amount = parseFloat(form.currentPayment);
    if (!amount || amount <= 0) {
      setErr("Enter a valid payment amount.");
      return;
    }
    setRzpProcessing(true);
    setErr("");
    try {
      const { data: orderData } = await apiClient.post("/payments/create-order", {
        studentId: selectedStudent._id,
        amount,
        description: form.comments || "Fee Payment",
        courseAssignmentId: selectedAssignment?._id || undefined,
      });

      openCheckout(
        orderData,
        async (response) => {
          try {
            const { data: verifyData } = await apiClient.post("/payments/verify", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              transactionId: orderData.transactionId,
            });
            const txn = verifyData.transaction;
            if (txn?.paymentType === "lms_payment" && txn.commissionAmount > 0) {
              setLastPaymentInfo({
                amount: txn.amount,
                commission: txn.commissionAmount,
                gatewayFee: txn.gatewayFee,
                netPayable: txn.netPayable,
              });
            }
            setToast({ show: true, message: "Online payment collected successfully!", type: "success" });
            setForm(initialData);
            setSelectedStudent(null);
          } catch {
            setToast({ show: true, message: "Payment received but verification failed. Contact support.", type: "error" });
          }
          setRzpProcessing(false);
        },
        (error) => {
          setToast({ show: true, message: error?.reason || "Payment was cancelled or failed.", type: "error" });
          setRzpProcessing(false);
        },
      );
    } catch (ex) {
      const msg = ex?.response?.data?.message || "Failed to create payment order.";
      setToast({ show: true, message: msg, type: "error" });
      setRzpProcessing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isOnlineMode) {
      handleRazorpayPayment();
      return;
    }
    setDisabledSave(true);
    if (!selectedStudent) {
      setErr("Select a valid student before collecting fee.");
      setDisabledSave(false);
      return;
    }
    const payload = { ...form, studentId: selectedStudent._id, courseAssignmentId: selectedAssignment?._id || undefined };
    let dataToSend = payload;
    let headers = {};
    if (form.refImage && typeof form.refImage !== "string") {
      dataToSend = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        if (value !== undefined && value !== null) dataToSend.append(key, value);
      });
      if (form.refImage) dataToSend.append("refImage", form.refImage);
      headers["Content-Type"] = "multipart/form-data";
    }
    try {
      if (isEditMode && feeId) {
        await apiClient.put(`/fees/${feeId}`, dataToSend, { headers });
        setToast({ show: true, message: "Fee updated successfully!", type: "success" });
        setTimeout(() => navigate(-1), 1200);
      } else {
        const res = await apiClient.post("/fees/add", dataToSend, { headers });
        if (res.status === 201) {
          setToast({ show: true, message: "Fee collected successfully!", type: "success" });
          setForm(initialData);
          setSelectedStudent(null);
        }
      }
    } catch (ex) {
      setErr(ex?.response?.data?.message || (isEditMode ? "Failed to update fee." : "Failed to collect fee. Please try again."));
    }
    setDisabledSave(false);
  };

  const feeStatus = pendingFee === 0 && totalFee > 0
    ? { key: "active", label: "Paid" }
    : paidFee > 0
    ? { key: "pending", label: "Partial" }
    : { key: "inactive", label: "Pending" };

  return (
    <div className="fee-form-page">
      {/* Edit mode loading overlay */}
      {editLoading && (
        <div className="fee-form-edit-loading">
          <Spinner animation="border" size="sm" /> Loading fee details...
        </div>
      )}
      {/* Header */}
      <div className="fee-form-header">
        <div className="fee-form-header__left">
          <BackButton onClick={() => navigate(-1)} />
          <div>
            <h2 className="fee-form-header__title">{isEditMode ? "Edit Fee" : "Collect Fee"}</h2>
            <p className="fee-form-header__desc">
              {isEditMode ? "Update the details of this fee transaction" : "Record a new fee payment from a student"}
            </p>
          </div>
        </div>
        <div className="fee-form-header__actions">
          <button className="fee-form-btn--cancel" type="button" onClick={onCancel}>Cancel</button>
          <AppButton
            onClick={handleSubmit}
            disabled={!selectedStudent || disabledSave || rzpProcessing || rzpLoading || editLoading}
          >
            {(rzpProcessing || rzpLoading) ? (
              <><Spinner size="sm" className="me-1" /> Processing...</>
            ) : isEditMode ? (
              <><CreditCard size={16} className="me-1" /> Save Changes</>
            ) : isOnlineMode ? (
              <><Wifi size={16} className="me-1" /> Pay via Razorpay</>
            ) : (
              <><CreditCard size={16} className="me-1" /> Collect Fee</>
            )}
          </AppButton>
        </div>
      </div>

      <div className="fee-form-layout">
        {/* Left column – Form */}
        <div className="fee-form-main">
          {/* Student Card */}
          <AppCard className="fee-form-card">
            <AppCardContent>
              <div className="fee-form-card__header">
                <User size={18} />
                <h3>Student</h3>
              </div>

              {selectedStudent ? (
                <div className="fee-form-selected-student">
                  <UserAvatar name={selectedStudent.name} size="md" />
                  <div className="fee-form-selected-student__info">
                    <span className="fee-form-selected-student__name">{selectedStudent.name}</span>
                    <span className="fee-form-selected-student__meta">
                      {selectedStudent.mobile}{selectedStudent.email ? ` • ${selectedStudent.email}` : ""}
                    </span>
                  </div>
                  {/* Don't allow changing the student in edit mode */}
                  {!isEditMode && (
                    <button className="fee-form-selected-student__clear" onClick={clearStudent} title="Change student">
                      <X size={16} />
                    </button>
                  )}
                </div>
              ) : (
                <div className="fee-form-search" ref={searchRef}>
                  <Search size={16} className="fee-form-search__icon" />
                  <input
                    type="text"
                    className="fee-form-search__input"
                    placeholder="Search by name or mobile..."
                    value={studentSearch}
                    onChange={(e) => handleStudentSearch(e.target.value)}
                    autoComplete="off"
                  />
                  {loadingStudent && <Spinner size="sm" className="fee-form-search__spinner" />}
                  {studentSearch && studentResults.length > 0 && (
                    <div className="fee-form-search__dropdown">
                      {studentResults.map((stu) => (
                        <div key={stu._id} className="fee-form-search__item" onClick={() => handleStudentSelect(stu)}>
                          <UserAvatar name={stu.name} size="sm" />
                          <div>
                            <div className="fee-form-search__item-name">{stu.name}</div>
                            <div className="fee-form-search__item-meta">{stu.mobile}</div>
                          </div>
                          {Number(stu.pendingFees) > 0 && (
                            <span className="fee-form-search__item-due">
                              {formatCurrency(stu.pendingFees)} due
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </AppCardContent>
          </AppCard>

          {/* Course Selector Card */}
          {selectedStudent && courseAssignments.length > 0 && (
            <AppCard className="fee-form-card">
              <AppCardContent>
                <div className="fee-form-card__header">
                  <FileText size={18} />
                  <h3>Select Course</h3>
                </div>
                <div className="fee-form-courses">
                  {courseAssignments.map((ca) => {
                    const isSelected = selectedAssignment?._id === ca._id;
                    const caPending = Number(ca.pendingFees || 0);
                    const caPaid = Number(ca.paidFees || 0);
                    const caTotal = Number(ca.totalFees || 0);
                    const isSP = ca.courseId?.courseType === 'self-paced';
                    return (
                      <div
                        key={ca._id}
                        className={`fee-form-course-item${isSelected ? " fee-form-course-item--selected" : ""}${caPending <= 0 ? " fee-form-course-item--paid" : ""}`}
                        onClick={() => {
                          if (caPending <= 0) return;
                          setSelectedAssignment(ca);
                          // For self-paced, lock amount to full pending
                          if (isSP) {
                            setForm((prev) => ({ ...prev, currentPayment: String(caPending) }));
                          } else {
                            setForm((prev) => ({ ...prev, currentPayment: "" }));
                          }
                        }}
                      >
                        <div className="fee-form-course-item__info">
                          <span className="fee-form-course-item__name">
                            {ca.courseId?.name || "Unknown Course"}
                            {isSP && <span className="fee-form-course-item__tag">Self-paced</span>}
                          </span>
                          {ca.batchId?.name && (
                            <span className="fee-form-course-item__batch">{ca.batchId.name}</span>
                          )}
                        </div>
                        <div className="fee-form-course-item__fees">
                          {caPending > 0 ? (
                            <span className="fee-form-course-item__pending">
                              {formatCurrency(caPending)} due
                            </span>
                          ) : (
                            <span className="fee-form-course-item__paid-badge">Paid</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </AppCardContent>
            </AppCard>
          )}

          {/* Payment Details Card */}
          <AppCard className="fee-form-card">
            <AppCardContent>
              <div className="fee-form-card__header">
                <IndianRupee size={18} />
                <h3>Payment Details</h3>
              </div>
              {feeLoading ? (
                <div className="fee-form-loading"><Spinner size="sm" /> Loading student details...</div>
              ) : (
                <div className="fee-form-grid">
                  <div className="fee-form-field">
                    <label>Payment Amount</label>
                    <input
                      type="number"
                      name="currentPayment"
                      value={form.currentPayment}
                      onChange={handleChange}
                      min={1}
                      max={pendingFee || undefined}
                      placeholder={isSelfPaced ? `Full payment: ₹${pendingFee}` : "Enter amount"}
                      required
                      disabled={!selectedStudent || isSelfPaced || isRazorpayTxn}
                      readOnly={isSelfPaced || isRazorpayTxn}
                    />
                    {isRazorpayTxn && (
                      <span className="fee-form-field__hint">Amount cannot be changed for online payments</span>
                    )}
                    {isSelfPaced && !isRazorpayTxn && pendingFee > 0 && (
                      <span className="fee-form-field__hint">Self-paced courses require full payment</span>
                    )}
                  </div>
                  <div className="fee-form-field">
                    <label>Due After Payment</label>
                    <input type="text" value={due < 0 ? formatCurrency(0) : formatCurrency(due)} readOnly className="readonly" />
                  </div>
                  <div className="fee-form-field">
                    <label>Payment Mode</label>
                    <select name="paymentMode" value={form.paymentMode} onChange={handleChange} required disabled={!selectedStudent || isRazorpayTxn}>
                      <option value="">Select mode</option>
                      {PAYMENT_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
                      {/* Keep existing mode if it's a razorpay/online mode */}
                      {isRazorpayTxn && form.paymentMode && !PAYMENT_MODES.includes(form.paymentMode) && (
                        <option value={form.paymentMode}>{form.paymentMode}</option>
                      )}
                    </select>
                  </div>
                  <div className="fee-form-field">
                    <label>Transaction ID</label>
                    <input
                      name="referenceNumber"
                      value={form.referenceNumber}
                      onChange={handleChange}
                      placeholder="Enter transaction ID"
                      required={form.paymentMode !== "Cash" && !isOnlineMode && !isRazorpayTxn}
                      disabled={!selectedStudent || form.paymentMode === "Cash" || isOnlineMode || isRazorpayTxn}
                    />
                  </div>
                  {!isOnlineMode && (
                    <div className="fee-form-field">
                      <label>Paid To</label>
                      <input name="paidTo" value={form.paidTo} onChange={handleChange} placeholder="Staff / recipient name" required={!isRazorpayTxn} disabled={!selectedStudent || isRazorpayTxn} />
                    </div>
                  )}
                  <div className="fee-form-field">
                    <label>Date of Payment</label>
                    <input type="date" name="date" value={form.date} onChange={handleChange} required disabled={!selectedStudent} />
                  </div>
                  {(due > 0 || form.nextPaymentDate) && (
                    <div className="fee-form-field">
                      <label>Next Payment Date</label>
                      <input type="date" name="nextPaymentDate" value={form.nextPaymentDate} onChange={handleChange} required={due > 0} disabled={!selectedStudent} />
                    </div>
                  )}
                </div>
              )}
            </AppCardContent>
          </AppCard>

          {/* Razorpay processing state */}
          {isOnlineMode && rzpProcessing && (
            <div className="fee-form-processing">
              <Spinner size="sm" /> Processing online payment via Razorpay...
            </div>
          )}

          {/* Reference Image Card — hide for online payments */}
          {!isOnlineMode && (
          <AppCard className="fee-form-card">
            <AppCardContent>
              <div className="fee-form-card__header">
                <ImagePlus size={18} />
                <h3>Reference Image</h3>
              </div>
              {selectedStudent ? (
                <UploadWithS3
                  s3KeyPrefix={`students/fee-screenshots/${selectedStudent._id}`}
                  accept="image/*"
                  onUpload={(key) => setForm((prev) => ({ ...prev, refImage: key }))}
                  onDelete={() => setForm((prev) => ({ ...prev, refImage: null }))}
                  value={form.refImage}
                />
              ) : (
                <p className="fee-form-placeholder">Select a student to upload reference image</p>
              )}
            </AppCardContent>
          </AppCard>
          )}

          {/* Comments Card */}
          <AppCard className="fee-form-card">
            <AppCardContent>
              <div className="fee-form-card__header">
                <MessageSquare size={18} />
                <h3>Comments / Notes</h3>
              </div>
              <textarea
                className="fee-form-textarea"
                name="comments"
                rows={3}
                value={form.comments}
                onChange={handleChange}
                placeholder="Add any notes or comments about this payment..."
                disabled={!selectedStudent}
              />
            </AppCardContent>
          </AppCard>

          {err && <div className="fee-form-error">{err}</div>}
        </div>

        {/* Right sidebar */}
        <div className="fee-form-sidebar">
          {/* Student Info */}
          <AppCard className="fee-form-sidebar-card">
            <AppCardContent>
              <h4 className="fee-form-sidebar__title">Student Info</h4>
              {selectedStudent ? (
                <div className="fee-form-sidebar-student">
                  <div className="fee-form-sidebar-student__top">
                    <UserAvatar name={selectedStudent.name} size="lg" />
                    <span className="fee-form-sidebar-student__name">{selectedStudent.name}</span>
                    <span className="fee-form-sidebar-student__email">{selectedStudent.email}</span>
                    {selectedStudent.mobile && (
                      <span className="fee-form-sidebar-student__phone">{selectedStudent.mobile}</span>
                    )}
                  </div>
                  <Separator className="my-3" />
                  <div className="fee-form-sidebar-info">
                    {selectedStudent.course && (
                      <div className="fee-form-sidebar-info__row">
                        <span className="fee-form-sidebar-info__label">Course</span>
                        <span className="fee-form-sidebar-info__value">
                          {selectedAssignment?.courseId?.name || selectedStudent.course?.name || selectedStudent.course}
                        </span>
                      </div>
                    )}
                    {(selectedAssignment?.batchId?.name || selectedStudent.batch) && (
                      <div className="fee-form-sidebar-info__row">
                        <span className="fee-form-sidebar-info__label">Batch</span>
                        <span className="fee-form-sidebar-info__value">
                          {selectedAssignment?.batchId?.name || selectedStudent.batch?.name || selectedStudent.batch}
                        </span>
                      </div>
                    )}
                    <Separator className="my-2" />
                    <div className="fee-form-sidebar-info__row">
                      <span className="fee-form-sidebar-info__label">Total Fees</span>
                      <span className="fee-form-sidebar-info__value">{formatCurrency(totalFee)}</span>
                    </div>
                    <div className="fee-form-sidebar-info__row">
                      <span className="fee-form-sidebar-info__label">Paid</span>
                      <span className="fee-form-sidebar-info__value fee-form-sidebar-info__value--paid">{formatCurrency(paidFee)}</span>
                    </div>
                    <div className="fee-form-sidebar-info__row">
                      <span className="fee-form-sidebar-info__label">Pending</span>
                      <span className="fee-form-sidebar-info__value fee-form-sidebar-info__value--pending">{formatCurrency(pendingFee)}</span>
                    </div>
                    <div className="fee-form-sidebar-info__row">
                      <span className="fee-form-sidebar-info__label">Status</span>
                      <StatusBadge status={feeStatus.key}>{feeStatus.label}</StatusBadge>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="fee-form-placeholder">Select a student to see their details</p>
              )}
            </AppCardContent>
          </AppCard>

          {/* Payment Summary */}
          {selectedStudent && form.currentPayment && (
            <AppCard className="fee-form-sidebar-card">
              <AppCardContent>
                <h4 className="fee-form-sidebar__title">Payment Summary</h4>
                <div className="fee-form-sidebar-info">
                  <div className="fee-form-sidebar-info__row">
                    <span className="fee-form-sidebar-info__label">Amount</span>
                    <span className="fee-form-sidebar-info__value">{formatCurrency(form.currentPayment)}</span>
                  </div>
                  {form.paymentMode && (
                    <div className="fee-form-sidebar-info__row">
                      <span className="fee-form-sidebar-info__label">Mode</span>
                      <span className="fee-form-sidebar-info__value">{form.paymentMode}</span>
                    </div>
                  )}
                  <div className="fee-form-sidebar-info__row">
                    <span className="fee-form-sidebar-info__label">Date</span>
                    <span className="fee-form-sidebar-info__value">
                      {form.date ? new Date(form.date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", timeZone: "Asia/Kolkata" }) : "-"}
                    </span>
                  </div>
                  {form.paidTo && (
                    <div className="fee-form-sidebar-info__row">
                      <span className="fee-form-sidebar-info__label">Paid To</span>
                      <span className="fee-form-sidebar-info__value">{form.paidTo}</span>
                    </div>
                  )}
                  <Separator className="my-2" />
                  <div className="fee-form-sidebar-info__row">
                    <span className="fee-form-sidebar-info__label">Due After</span>
                    <span className="fee-form-sidebar-info__value fee-form-sidebar-info__value--pending">
                      {due <= 0 ? formatCurrency(0) : formatCurrency(due)}
                    </span>
                  </div>
                </div>
              </AppCardContent>
            </AppCard>
          )}

          {/* LMS Payment Info */}
          {paymentConfig?.paymentType === "lms_payment" && isOnlineMode && (
            <AppCard className="fee-form-sidebar-card">
              <AppCardContent>
                <h4 className="fee-form-sidebar__title">
                  <Info size={14} className="me-1" />
                  LMS Payment Info
                </h4>
                <p style={{ fontSize: "0.75rem", color: "var(--text-secondary)", margin: "0.5rem 0 0" }}>
                  This payment is processed via the platform gateway. A commission will be deducted and the net amount will appear in your Earnings page as a pending payout.
                </p>
              </AppCardContent>
            </AppCard>
          )}

          {/* Last Payment Commission Breakdown */}
          {lastPaymentInfo && (
            <AppCard className="fee-form-sidebar-card">
              <AppCardContent>
                <h4 className="fee-form-sidebar__title">Commission Breakdown</h4>
                <div className="fee-form-sidebar-info">
                  <div className="fee-form-sidebar-info__row">
                    <span className="fee-form-sidebar-info__label">Collected</span>
                    <span className="fee-form-sidebar-info__value">{formatCurrency(lastPaymentInfo.amount)}</span>
                  </div>
                  <div className="fee-form-sidebar-info__row">
                    <span className="fee-form-sidebar-info__label">Commission</span>
                    <span className="fee-form-sidebar-info__value fee-form-sidebar-info__value--pending">
                      -{formatCurrency(lastPaymentInfo.commission)}
                    </span>
                  </div>
                  {lastPaymentInfo.gatewayFee > 0 && (
                    <div className="fee-form-sidebar-info__row">
                      <span className="fee-form-sidebar-info__label">Gateway Fee</span>
                      <span className="fee-form-sidebar-info__value fee-form-sidebar-info__value--pending">
                        -{formatCurrency(lastPaymentInfo.gatewayFee)}
                      </span>
                    </div>
                  )}
                  <Separator className="my-2" />
                  <div className="fee-form-sidebar-info__row">
                    <span className="fee-form-sidebar-info__label">Net Payable</span>
                    <span className="fee-form-sidebar-info__value fee-form-sidebar-info__value--paid">
                      {formatCurrency(lastPaymentInfo.netPayable)}
                    </span>
                  </div>
                </div>
              </AppCardContent>
            </AppCard>
          )}
        </div>
      </div>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default FeeCollectionForm;
